import { useState, useEffect } from "react";
import { Stack, Box, Typography, Container } from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../../../hooks/useToastData";
import { ToastNotification } from "components/common/ToastNotification";
import { UploadActionButtons } from "./UploadActionButtons";
import GeekPreviewPlayer from "../../GeekPreviewPlayer";
import ResumeUpload from "../ResumeUpload";
import { updateApplicantPublic } from "../../../apiconfig/api";
import ReactGA from "react-ga4";
import useMediaUpload from "../../../hooks/useMediaUpload";

const UploadVideo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCamera = location.state?.camera || false;
  const videoBlob = location.state?.blob;
  const videoBlobUrl = videoBlob ? URL.createObjectURL(videoBlob) : null;

  const [resumeDocumentId, setResumeDocumentId] = useState<string | null>(null);
  const [resumeUploadError, setResumeUploadError] = useState<string | null>(null);
  const [isUpdatingApplicant, setIsUpdatingApplicant] = useState(false);
  const { toast, handleCloseToast, showErrorToast } =
    useToast();

  const { 
    uploadState,
    uploadProgress,
    uploadMedia,
    convertBlobToFile
  } = useMediaUpload();

  const isUploading = uploadState === "uploading";

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (videoBlobUrl) {
        URL.revokeObjectURL(videoBlobUrl);
        console.log('Cleanup: Revoked video blob URL');
      }
    };
  }, [videoBlobUrl]);

  const handleUploadClick = async () => {
    ReactGA.event({
      category: "Upload",
      action: "Upload Clicked",
      label: "Upload Clicked",
    });
    
    if (!videoBlob) {
      showErrorToast("No video to upload. Please record or choose a video first.");
      return;
    }

    const file = convertBlobToFile(videoBlob, "video");
    const mediaId = await uploadMedia(file, isCamera);
    
    try {
      setIsUpdatingApplicant(true);  
      const applicantData = localStorage.getItem('applicantData');
      if (!applicantData) { throw new Error('No applicant data found'); }
      const parsedData = JSON.parse(applicantData);
      const applicantUuid = parsedData.applicantUuid;
      if (!applicantUuid) { throw new Error('Applicant UUID not found'); }
      const updatePayload: any = { videoUuid: mediaId };
      if (resumeDocumentId) { updatePayload.resumeDocumentId = resumeDocumentId; }
      await updateApplicantPublic(applicantUuid, updatePayload);
      navigate('/thank-you', { 
        state: { 
          userName: parsedData.fullName,
          fromVideoUpload: true 
        },
        replace: true
      });
    } catch (error) {
      console.error('Error updating applicant:', error);
      showErrorToast('Failed to complete application. Please try again.');
    } finally {
      setIsUpdatingApplicant(false);
    }
  };

  const handleRedo = () => {
    ReactGA.event({
      category: "Redo",
      action: "Redo Clicked",
      label: "Redo Clicked",
    });
    if (isCamera) {
      navigate("/apply/record", { state: { isCamera, isMuted: false } });
    } else {
      navigate(-1);
    }
  };

  const handleResumeUploadComplete = (documentId: string) => {
    setResumeDocumentId(documentId);
    setResumeUploadError(null);
  };
  
  const handleResumeUploadError = (error: string) => {
    setResumeUploadError(error);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Stack spacing={3} sx={{ flexDirection: "column", px: 2, height: '100vh' }}>
        <Typography level="h2" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
          Review Video
        </Typography>
        {/* Video Player with Upload Overlay */}
        <Box sx={{ position: 'relative'}}>
          <Box
            sx={{
              width: { xs: "100%", sm: "60%", md: "40%" },
              mx: "auto",
              aspectRatio: "3/4",
              maxWidth: { xs: "250px", sm: "400px" },
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
              {videoBlob && videoBlobUrl ? (
                <GeekPreviewPlayer
                  localVideoUrl={videoBlobUrl}
                  playing={true}
                  aspectRatio="3/4"
                  showMuteButton={true}
                  showScrubber={false}
                />
              ) : (
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "3/4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "black",
                  color: "white",
                  borderRadius: "16px",
                }}
              >
                <Typography level="h3">No video available</Typography>
              </Box>
            )}
          </Box>
          
          {(isUploading || isUpdatingApplicant) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.5)",
                borderRadius: "16px",
                zIndex: 10,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(0,0,0,0.8)",
                  borderRadius: "12px",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  minWidth: "200px",
                }}
              >
                {isUploading && (
                  <>
                    {/* Progress Bar */}
                    <Box
                      sx={{
                        width: "100%",
                        height: "8px",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${uploadProgress}%`,
                          height: "100%",
                          backgroundColor: "#2E2D7E",
                          borderRadius: "4px",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                    <Typography
                      level="body-lg"
                      sx={{
                        color: "white",
                        fontWeight: "md",
                        textAlign: "center",
                      }}
                    >
                      Uploading Video... {Math.round(uploadProgress)}%
                    </Typography>
                  </>
                )}
                
                {isUpdatingApplicant && (
                  <Typography
                    level="body-lg"
                    sx={{
                      color: "white",
                      fontWeight: "md",
                      textAlign: "center",
                    }}
                  >
                    Completing application...
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
        
        <Box>
          <ResumeUpload
            onUploadComplete={handleResumeUploadComplete}
            onUploadError={handleResumeUploadError}
            disabled={isUploading || isUpdatingApplicant}
          />
          
          {resumeUploadError && (
            <Typography
              level="body-sm"
              sx={{
                color: '#ef4444',
                mt: 1,
                fontSize: '14px'
              }}
            >
              {resumeUploadError}
            </Typography>
          )}
        </Box>

        {/* Warning message when upload is in progress */}
        {/* {(isUploading || isUpdatingApplicant) && ( */}
        { true && (
          <Box
            sx={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                fontSize: '24px',
              }}
            >
              ⚠️
            </Box>
            <Typography
              level="body-md"
              sx={{
                color: '#92400E',
                fontWeight: 600,
                flex: 1,
              }}
            >
              Do not navigate away from this page until upload is complete.
            </Typography>
          </Box>
        )}

        <UploadActionButtons
          isUploading={isUploading || isUpdatingApplicant}
          recordedVideo={videoBlob}
          onUpload={handleUploadClick}
          onRedo={handleRedo}
        />

        <ToastNotification
          open={toast.open}
          message={toast.message}
          color={toast.color}
          onClose={handleCloseToast}
        />
      </Stack>
    </Container>
  );
};

export default UploadVideo;
